import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import React, { useMemo, useState } from 'react';
import { faChevronLeft, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addDays, addHours, addMinutes, format, isAfter, setMinutes } from 'date-fns';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_green.css';
import newDate from '../../utils/newDate';
import { getConfirmation } from '../../utils/ui';
import StatusEmptyCard from './StatusEmptyCard';
import {
    CREATE_FIXTURE_NOTICE,
    CREATE_LIVE_EMPTY_NOTICE,
    CREATE_SHOP_JOB_CONFIRM,
    CREATE_SHOP_JOB_CONFIRM_TITLE,
    SHOP_CREATE_SOT_VALUES,
    TRUE_EMPTY_WALK_CONFIRM,
    TRUE_EMPTY_WALK_CONFIRM_TITLE,
    createShopJobFromForm,
    isTrueEmptyWalkFixture,
    shopCreateCallableError,
    shopCreateCopy,
    shopCreateFixtureDetail,
    shopCreateGuard,
    shopCreatePath,
} from './createShopJob';

const orderSchema = Yup.object({
    contact: Yup.string().required('This input is required'),
    customerName: Yup.string().required('This input is required'),
    type: Yup.string().required('This input is required'),
    damage: Yup.string().required('This input is required'),
    notes: Yup.string().required('This input is required'),
    date: Yup.string()
        .required('This input is required')
        .test(
            'is-greater',
            'Appointment time has to be at least 24hrs in advance',
            function (value) {
                return isAfter(
                    newDate(value).getTime(),
                    new Date().setDate(new Date().getDate() + 1),
                );
            },
        ),
    vehicle: Yup.object({
        year: Yup.number().required('This input is required'),
        make: Yup.string().required('This input is required'),
        model: Yup.string().required('This input is required'),
        insurance: Yup.string().required('This input is required'),
    }),
});

const damageTypeOptions = [
    { value: 'Front', title: 'Front' },
    { value: 'Rear', title: 'Rear' },
    { value: '1/4 Glass', title: '1/4 Glass' },
    { value: 'Other', title: 'Other' },
];

const jobTypeOptions = [
    { value: 'Remove', title: 'Remove' },
    { value: 'Install', title: 'Install' },
    { value: 'Remove & Install', title: 'Remove & Install' },
    { value: 'Cliff test', title: 'Cliff test' },
];

const dropoff = [
    { value: 'Currently dropped', title: 'Currently dropped' },
    { value: 'Will be dropped', title: 'Will be dropped' },
    { value: 'Not dropping', title: 'Not dropping' },
];

function defaultAppointment() {
    return format(addHours(setMinutes(addDays(new Date(), 1), 0), 1), 'yyyy-MM-dd HH:mm');
}

function getMinDate() {
    const curDate = new Date();
    const nextDate = addMinutes(
        addDays(curDate, 1),
        curDate.getMinutes() % 15 === 0 ? 0 : 15 - (curDate.getMinutes() % 15),
    );
    return format(nextDate, 'yyyy-MM-dd HH:mm');
}

const CreateJobModal = ({
    shopUser,
    shopId,
    regionId,
    offline,
    fixture,
    fixtureSource,
    liveEmpty,
    regionPhone,
    onClose,
    onCreated,
    createJob = createShopJobFromForm,
    confirmCreate = getConfirmation,
}) => {
    const openingGuard = useMemo(
        () =>
            shopCreateGuard({
                fixture,
                offline,
                shopUser,
                shopId,
                regionId,
            }),
        [fixture, offline, shopUser, shopId, regionId],
    );
    const [surface, setSurface] = useState(
        openingGuard && openingGuard !== 'sot' && openingGuard !== 'preview'
            ? openingGuard
            : null,
    );
    const [createError, setCreateError] = useState('');
    const [createdResult, setCreatedResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [validatingAddShop, setValidatingAddShop] = useState(false);

    const copy = shopCreateCopy(surface);
    const fixtureNotice = CREATE_FIXTURE_NOTICE[fixture] || (liveEmpty ? CREATE_LIVE_EMPTY_NOTICE : null);
    const createSotFixture = SHOP_CREATE_SOT_VALUES.includes(fixture);
    const walkFixture = isTrueEmptyWalkFixture(fixture);
    const fixtureDetail =
        createSotFixture || walkFixture ? shopCreateFixtureDetail(walkFixture ? fixture : fixtureSource) : undefined;
    const createPath = shopCreatePath(fixture, liveEmpty);

    const close = () => {
        onClose();
    };

    const finishCreated = (result = createdResult) => {
        if (onCreated) onCreated(result);
        else onClose();
    };

    const retryOffline = () => {
        const stillOffline = typeof navigator !== 'undefined' ? navigator.onLine === false : false;
        if (stillOffline) {
            setSurface('offline');
            return;
        }
        const next = shopCreateGuard({
            fixture,
            offline: false,
            shopUser,
            shopId,
            regionId,
        });
        setSurface(next === 'denied' || next === 'fixture-region' ? next : null);
    };

    const submitCreate = async (values) => {
        const guard = shopCreateGuard({
            fixture,
            offline,
            shopUser,
            shopId,
            regionId,
        });
        if (guard) {
            setSurface(guard);
            return;
        }
        if (walkFixture) {
            const confirmedWalk = await confirmCreate(
                TRUE_EMPTY_WALK_CONFIRM_TITLE,
                TRUE_EMPTY_WALK_CONFIRM,
                {
                    confirmButtonText: 'Show confirm step',
                    cancelButtonText: 'Cancel',
                },
            );
            if (!confirmedWalk) {
                setCreateError('');
                setSurface('cancelled');
                return;
            }
            setSurface(fixture);
            return;
        }
        const confirmed = await confirmCreate(
            CREATE_SHOP_JOB_CONFIRM_TITLE,
            CREATE_SHOP_JOB_CONFIRM,
            {
                confirmButtonText: 'Create pending job',
                cancelButtonText: 'Cancel',
            },
        );
        if (!confirmed) {
            setCreateError('');
            setSurface('cancelled');
            return;
        }
        setBusy(true);
        setCreateError('');
        try {
            const result = await createJob(values, undefined, regionId);
            setSurface('pending');
            setCreateError(result.jobId ? `Job ${result.jobId} is pending.` : '');
            setCreatedResult(result);
        } catch (error) {
            setSurface('error');
            setCreateError(shopCreateCallableError(error));
        } finally {
            setBusy(false);
        }
    };

    const handleCalendarChange = (formik, [date]) => {
        if (!date || !Date.parse(date)) {
            formik.setFieldValue('date', '');
            return;
        }
        formik.setFieldValue('date', format(new Date(date), 'yyyy-MM-dd HH:mm'));
    };

    return (
        <div
            className="fixed w-full h-full top-0 left-0 flex z-50"
            data-testid="create-job-modal"
            data-create-surface={surface || 'form'}
            data-create-path={createPath}
        >
            <div className="fixed w-full h-full overflow-y-auto bg-gray-100 bg-cover pb-12">
                <div className="fixed justify-start p-2 border-b border-solid border-gray-300 bg-white w-full">
                    <button
                        className="text-blue-500 background-transparent font-bold text-sm outline-none focus:outline-none"
                        type="button"
                        onClick={close}
                    >
                        <FontAwesomeIcon className="text-base mr-1" icon={faChevronLeft} />
                        <span>Back</span>
                    </button>
                </div>
                <div className="md:mt-10 mx-auto max-w-7xl">
                    <div className="pt-14 container mx-auto h-auto pb-4">
                        {copy ? (
                            <div className="flex flex-col items-center mt-6">
                                <StatusEmptyCard
                                    copy={copy}
                                    detail={
                                        surface === 'error' || surface === 'pending'
                                            ? createError || fixtureDetail
                                            : fixtureDetail
                                    }
                                    onAction={
                                        surface === 'offline'
                                            ? retryOffline
                                            : surface === 'error' || surface === 'cancelled'
                                              ? () => {
                                                    setCreateError('');
                                                    setSurface(null);
                                                }
                                              : surface === 'pending'
                                                ? () => finishCreated()
                                                : surface === 'denied' || surface === 'fixture-region' || surface === 'preview'
                                                  ? close
                                                  : undefined
                                    }
                                />
                            </div>
                        ) : (
                            <>
                                <div className="text-center md:mb-5">
                                    <div
                                        className="p-2 bg-indigo-800 items-center text-indigo-100 leading-none lg:rounded-full flex lg:inline-flex"
                                        role="alert"
                                    >
                                        <span className="flex rounded-full bg-indigo-500 uppercase px-2 py-1 text-xs font-bold mr-3">
                                            Notice
                                        </span>
                                        <span className="font-semibold mr-2 flex-auto">
                                            Appointments sooner than 24 hours in advance cannot be
                                            scheduled on this system and should be called in.
                                            createShopJob writes pending only.
                                        </span>
                                        {regionPhone ? (
                                            <div className="mr-2 flex">
                                                <FontAwesomeIcon
                                                    className="text-base mr-1"
                                                    icon={faPhone}
                                                />
                                                <span>{regionPhone}</span>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                {fixtureNotice ? (
                                    <p
                                        className="text-center text-gray-700 text-sm px-6 mb-4"
                                        data-testid="create-fixture-notice"
                                    >
                                        {fixtureNotice}
                                    </p>
                                ) : null}
                                <div className="md:mt-0 md:col-span-2 text-left">
                                    <div className="shadow overflow-hidden sm:rounded-md">
                                        <Formik
                                            initialValues={{
                                                type: '',
                                                damage: '',
                                                date: defaultAppointment(),
                                                customerName: '',
                                                shop: shopId,
                                                contact: '',
                                                vehicle: {
                                                    year: '',
                                                    make: '',
                                                    model: '',
                                                    insurance: '',
                                                },
                                                notes: '',
                                                waiting: false,
                                                dropped: 'Currently dropped',
                                            }}
                                            validateOnBlur={false}
                                            validateOnChange={validatingAddShop}
                                            onSubmit={submitCreate}
                                            validationSchema={orderSchema}
                                        >
                                            {(formik) => (
                                                <>
                                                    <div className="px-4 py-5 bg-white sm:p-6">
                                                        <div className="grid grid-cols-6 gap-6">
                                                            <div className="col-span-6 sm:col-span-3">
                                                                <label
                                                                    htmlFor="job_type"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Job Type
                                                                </label>
                                                                <select
                                                                    name="job_type"
                                                                    id="job_type"
                                                                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'type',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                >
                                                                    <option value="">
                                                                        Select job type
                                                                    </option>
                                                                    {jobTypeOptions.map((type) => (
                                                                        <option
                                                                            key={type.value}
                                                                            value={type.value}
                                                                        >
                                                                            {type.title}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {formik.errors.type ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.type}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-3">
                                                                <label
                                                                    htmlFor="damage_type"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Service Type
                                                                </label>
                                                                <select
                                                                    name="damage_type"
                                                                    id="damage_type"
                                                                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'damage',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                >
                                                                    <option value="">
                                                                        Select service type
                                                                    </option>
                                                                    {damageTypeOptions.map((type) => (
                                                                        <option
                                                                            key={type.value}
                                                                            value={type.value}
                                                                        >
                                                                            {type.title}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {formik.errors.damage ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.damage}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-3">
                                                                <label
                                                                    htmlFor="date"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Date
                                                                </label>
                                                                <Flatpickr
                                                                    data-enable-time
                                                                    value={formik.values.date}
                                                                    options={{
                                                                        minuteIncrement: 15,
                                                                        altInput: true,
                                                                        altFormat: 'M j\\, Y h\\:i K',
                                                                        dateFormat: 'Y\\-m\\-d\\ H\\:i',
                                                                        minDate: getMinDate(),
                                                                    }}
                                                                    name="date"
                                                                    className="p-2 mt-1 w-full rounded-md border-lightGray shadow-sm text-sm"
                                                                    onChange={(date) => {
                                                                        handleCalendarChange(
                                                                            formik,
                                                                            date,
                                                                        );
                                                                    }}
                                                                />
                                                                {formik.errors.date ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.date}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                                                                <label
                                                                    htmlFor="make"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Vehicle Make
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    name="make"
                                                                    id="make"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'vehicle.make',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                                />
                                                                {formik.errors.vehicle?.make ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.vehicle.make}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-3 lg:col-span-1">
                                                                <label
                                                                    htmlFor="year"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Vehicle Year
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    name="year"
                                                                    id="year"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'vehicle.year',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                                />
                                                                {formik.errors.vehicle?.year ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.vehicle.year}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                                                                <label
                                                                    htmlFor="model"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Vehicle Model
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    name="model"
                                                                    id="model"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'vehicle.model',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                                />
                                                                {formik.errors.vehicle?.model ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.vehicle.model}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                                                                <label
                                                                    htmlFor="insurance"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Vehicle Insurance
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    name="insurance"
                                                                    id="insurance"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'vehicle.insurance',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                                />
                                                                {formik.errors.vehicle?.insurance ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.vehicle.insurance}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                                                                <label
                                                                    htmlFor="contact"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Primary Contact
                                                                </label>
                                                                <input
                                                                    required
                                                                    type="text"
                                                                    name="contact"
                                                                    id="contact"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'contact',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                                />
                                                                {formik.errors.contact ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.contact}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-3">
                                                                <label
                                                                    htmlFor="customerName"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Customer Name
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    name="customerName"
                                                                    id="customerName"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'customerName',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                                />
                                                                {formik.errors.customerName ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.customerName}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 sm:col-span-3">
                                                                <label
                                                                    htmlFor="dropoff"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Vehicle Dropoff
                                                                </label>
                                                                <select
                                                                    name="dropoff"
                                                                    id="dropoff"
                                                                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'dropped',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                >
                                                                    <option value="">
                                                                        Select Dropoff
                                                                    </option>
                                                                    {dropoff.map((type) => (
                                                                        <option
                                                                            key={type.value}
                                                                            value={type.value}
                                                                        >
                                                                            {type.title}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {formik.errors.dropped ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.dropped}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                            <div className="col-span-6 my-auto">
                                                                <div>
                                                                    <span className="text-sm font-medium text-gray-700">
                                                                        Available to pick up:
                                                                    </span>
                                                                    <Field
                                                                        className="ml-3"
                                                                        type="checkbox"
                                                                        name="waiting"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-span-6">
                                                                <label
                                                                    htmlFor="notes"
                                                                    className="block text-sm font-medium text-gray-700"
                                                                >
                                                                    Notes
                                                                </label>
                                                                <input
                                                                    required
                                                                    type="text"
                                                                    name="notes"
                                                                    id="notes"
                                                                    onChange={(event) =>
                                                                        formik.setFieldValue(
                                                                            'notes',
                                                                            event.target.value,
                                                                        )
                                                                    }
                                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                                />
                                                                {formik.errors.notes ? (
                                                                    <p className="text-red-500 text-xs italic mt-2">
                                                                        {formik.errors.notes}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                                                        <button
                                                            type="button"
                                                            disabled={busy}
                                                            onClick={() => {
                                                                const guard = shopCreateGuard({
                                                                    fixture,
                                                                    offline,
                                                                    shopUser,
                                                                    shopId,
                                                                    regionId,
                                                                });
                                                                if (guard) {
                                                                    setSurface(guard);
                                                                    return;
                                                                }
                                                                setValidatingAddShop(true);
                                                                formik.submitForm();
                                                            }}
                                                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                        >
                                                            {busy ? 'Creating…' : 'Create Job'}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </Formik>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateJobModal;
